import styled from 'styled-components';

import { UserType } from '../../../../types/UserType';
import { Text } from '../../../cdl/Text/Text';
import { identifyUserRole } from '../../helpers/identifyUserRole.helper';
import { translate } from '../../helpers/translate.helper';
import { Characters } from '../../helpers/unicode.helper';
import { useEnums } from '../../hooks/useEnums';
import { useRole } from '../../hooks/useRole';
import { IconSelector } from '../../icons/cdl/Selector';
import { Box } from '../../ui/Box';
import { theme } from '../../ui/theme';

import { SuperAdminSidebarMenuHeader } from './SuperAdminSidebarMenuHeader';

export const SidebarMenuHeaderContainer = styled(Box)`
    padding: ${theme.space[8]} ${theme.space[12]};
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: ${theme.space[8]};
`;

export const SidebarMenuChevronWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${theme.colors.foreground.subtle};
    flex-shrink: 0;
`;

const getCompanyTypeDisplay = (type: UserType) => {
    switch (type) {
        case UserType.CUSTOMER:
            return translate('global.coreData.customer');
        case UserType.SUPPLIER:
            return translate('global.coreData.supplier');
        default:
            return null;
    }
};

export const SidebarMenuHeader = () => {
    const role = useRole();
    const { getHumanReadableValue } = useEnums();

    if (role.isSuperAdmin()) {
        return <SuperAdminSidebarMenuHeader />;
    }

    const userRole = identifyUserRole(role.user.accessLevel, role.user.companyAccesses);
    const readableUserRole = getHumanReadableValue('role', userRole);

    const isTestCompany = role.group.testCompany;
    const companyType = getCompanyTypeDisplay(role.user.userType);

    return (
        <SidebarMenuHeaderContainer>
            <Box display="flex" flexDirection="column" alignItems="flex-start" gap={4} overflow="hidden">
                <Text
                    variant="extraSmall"
                    fontWeight={600}
                    textAlign="left"
                    style={{
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        hyphens: 'auto',
                    }}
                >
                    {role.group.name}
                </Text>

                <Text
                    variant="extraSmall"
                    textAlign="left"
                    color="foreground.muted"
                    style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}
                >
                    {readableUserRole}
                    {isTestCompany && companyType ? (
                        <>
                            {Characters.EN_SPACE}
                            {Characters.MIDDLE_DOT}
                            {Characters.EN_SPACE}
                            {companyType}
                        </>
                    ) : null}
                </Text>
            </Box>
            <SidebarMenuChevronWrapper>
                <IconSelector width={16} height={16} />
            </SidebarMenuChevronWrapper>
        </SidebarMenuHeaderContainer>
    );
};
